/**
 * Signature placements are stored relative to the *visual* (rotation-applied) page so the editor never has to
 * know about PDF user space. Values are fractions (0–1) of the visual page width/height, origin at the top-left.
 */
export interface PdfPlacement {
  id: string;
  pageIndex: number;
  x: number;
  y: number;
  /** Width/height as a fraction of the visual page at `scale === 1`. */
  baseW: number;
  baseH: number;
  scale: number;
  /** Copied from the session collection, which is capped and may evict entries. */
  dataUrl: string;
  signedBy: string;
}

/** Visual page size in PDF points (pdf.js viewport at scale 1, rotation applied). */
export interface PdfPageSize {
  width: number;
  height: number;
}

export interface PdfBox {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export type PdfRotation = 0 | 90 | 180 | 270;

/** CSS width of a page at 100% zoom; also the reference width for sizing a freshly inserted signature. */
export const PDF_PAGE_BASE_WIDTH = 620;
export const STAMP_FONT_SIZE = 8;
export const STAMP_MIN_FONT_SIZE = 4;
export const STAMP_LINE_HEIGHT = 10;
/** Space reserved under each signature for the "Digitally signed by" / "Date" lines, in points. */
export const STAMP_HEIGHT_PT = STAMP_LINE_HEIGHT * 2 + 2;
export const MIN_PLACEMENT_SCALE = 0.5;
export const PLACEMENT_SCALE_STEP = 0.1;
export const NUDGE_STEP = 0.01;
export const NUDGE_STEP_LARGE = 0.05;
const MAX_INITIAL_WIDTH = 0.25;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), Math.max(min, max));

export const getStampFraction = (page: PdfPageSize) => (page.height > 0 ? STAMP_HEIGHT_PT / page.height : 0);

export interface PdfStampTextLayout {
  /** Visual-page x position in PDF points. */
  x: number;
  /** Font size in PDF points. */
  size: number;
}

/**
 * Fits the stamp text on the visual page. It first scales down to the minimum font size; if that still does not
 * fit to the right of the signature, it moves left. Only when the text cannot fit anywhere at the minimum size
 * does it shrink below that size.
 */
export const getStampTextLayout = (
  startX: number,
  pageWidth: number,
  textWidthAtDefaultSize: number
): PdfStampTextLayout => {
  const width = Math.max(pageWidth, 0);
  const x = clamp(startX, 0, width);
  const textWidth = Math.max(textWidthAtDefaultSize, 0);
  if (textWidth === 0) return { x, size: STAMP_FONT_SIZE };

  const availableWidth = width - x;
  const sizeToFitAtStart = Math.min(STAMP_FONT_SIZE, (STAMP_FONT_SIZE * availableWidth) / textWidth);
  if (sizeToFitAtStart >= STAMP_MIN_FONT_SIZE) return { x, size: sizeToFitAtStart };

  const minSizeWidth = (textWidth * STAMP_MIN_FONT_SIZE) / STAMP_FONT_SIZE;
  if (minSizeWidth <= width) {
    return { x: Math.min(x, width - minSizeWidth), size: STAMP_MIN_FONT_SIZE };
  }

  // Extremely long text on a narrow page: keep the whole line visible, relaxing the minimum font size.
  return { x: 0, size: (STAMP_FONT_SIZE * width) / textWidth };
};

const getMaxScale = (placement: Pick<PdfPlacement, 'baseW' | 'baseH'>, page: PdfPageSize) => {
  const byWidth = placement.baseW > 0 ? 1 / placement.baseW : Infinity;
  const byHeight = placement.baseH > 0 ? (1 - getStampFraction(page)) / placement.baseH : Infinity;
  return Math.min(byWidth, byHeight);
};

/** Keeps the signature and its stamp fully on the page. */
export const clampPlacement = (placement: PdfPlacement, page: PdfPageSize): PdfPlacement => {
  const maxScale = getMaxScale(placement, page);
  const scale = Math.min(Math.max(placement.scale, MIN_PLACEMENT_SCALE), maxScale);
  const w = placement.baseW * scale;
  const h = placement.baseH * scale;
  return {
    ...placement,
    scale,
    x: clamp(placement.x, 0, 1 - w),
    y: clamp(placement.y, 0, 1 - h - getStampFraction(page)),
  };
};

export const resizePlacement = (placement: PdfPlacement, direction: 1 | -1, page: PdfPageSize) =>
  clampPlacement({ ...placement, scale: placement.scale + direction * PLACEMENT_SCALE_STEP }, page);

export const nudgePlacement = (placement: PdfPlacement, dx: number, dy: number, page: PdfPageSize) =>
  clampPlacement({ ...placement, x: placement.x + dx, y: placement.y + dy }, page);

export const movePlacement = (
  placement: PdfPlacement,
  pageIndex: number,
  x: number,
  y: number,
  sourcePage: PdfPageSize,
  destinationPage: PdfPageSize
) =>
  clampPlacement(
    {
      ...placement,
      pageIndex,
      x,
      y,
      // Keep the signature's physical dimensions when the destination has a different visual page aspect ratio.
      baseW: destinationPage.width > 0 ? (placement.baseW * sourcePage.width) / destinationPage.width : placement.baseW,
      baseH:
        destinationPage.height > 0 ? (placement.baseH * sourcePage.height) / destinationPage.height : placement.baseH,
    },
    destinationPage
  );

interface InitialPlacementArgs {
  id: string;
  pageIndex: number;
  page: PdfPageSize;
  signature: { dataUrl: string; width: number; height: number; signedBy: string };
  /** Vertical centre as a fraction of the page, usually the middle of what's on screen. */
  centerY?: number;
}

export const createInitialPlacement = ({ id, pageIndex, page, signature, centerY = 0.5 }: InitialPlacementArgs) => {
  const baseW = Math.min(signature.width / PDF_PAGE_BASE_WIDTH, MAX_INITIAL_WIDTH);
  const aspect = signature.width > 0 ? signature.height / signature.width : 0.5;
  const baseH = baseW * aspect * (page.height > 0 ? page.width / page.height : 1);
  return clampPlacement(
    {
      id,
      pageIndex,
      x: 0.5 - baseW / 2,
      y: centerY - baseH / 2,
      baseW,
      baseH,
      scale: 1,
      dataUrl: signature.dataUrl,
      signedBy: signature.signedBy,
    },
    page
  );
};

export const getPlacementSize = (placement: PdfPlacement) => ({
  w: placement.baseW * placement.scale,
  h: placement.baseH * placement.scale,
});

export const normalizeRotation = (angle: number): PdfRotation => {
  const normalized = (((Math.round(angle / 90) * 90) % 360) + 360) % 360;
  return normalized as PdfRotation;
};

/** Visual page size of a box once `/Rotate` is applied. */
export const getVisualSize = (box: PdfBox, rotation: PdfRotation): PdfPageSize => {
  const width = box.x1 - box.x0;
  const height = box.y1 - box.y0;
  return rotation === 90 || rotation === 270 ? { width: height, height: width } : { width, height };
};

/**
 * Maps a point measured in points from the visual top-left of the page into PDF user space. Content drawn at the
 * returned point with `rotate: degrees(rotation)` appears upright to the viewer.
 */
export const toPdfPoint = (box: PdfBox, rotation: PdfRotation, px: number, py: number) => {
  switch (rotation) {
    case 90:
      return { x: box.x0 + py, y: box.y0 + px };
    case 180:
      return { x: box.x1 - px, y: box.y0 + py };
    case 270:
      return { x: box.x1 - py, y: box.y1 - px };
    default:
      return { x: box.x0 + px, y: box.y1 - py };
  }
};
